import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import { useSelector } from "react-redux";
import { asList, useLoadIfEmpty } from "../../hooks/useLoadIfEmpty";
import { thunkFetchLoginMethods } from "../../store/markets/actions";
import {
  Checkbox,
  FormControl,
  IconButton,
  Input,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { AppState } from "../../store";
import { LoginMethod } from "../../models/Market";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import Divider from "@material-ui/core/Divider";

interface LoginMethodOrderedSelectProps extends Omit<SelectProps, "loading"> {
  label?: string;
  onSelected: (value: LoginMethod[]) => void;
  variant?: "filled" | "outlined" | "standard";
  value: Array<LoginMethod>;
  onlyAllowsIdSwitching?: boolean;
}

export const LoginMethodOrderedSelects: FunctionComponent<
  LoginMethodOrderedSelectProps
> = ({
  label = "LoginMethod",
  onSelected,
  onChange,
  variant = "standard",
  value = [] as Array<LoginMethod>,
  ...props
}) => {
  const loginMethods = asList<LoginMethod>(
    useSelector<AppState, unknown>(s => s.loginMethods)
  );

  useLoadIfEmpty(!loginMethods.length, thunkFetchLoginMethods);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let methods = event.target.value as Array<LoginMethod>;
      methods.forEach((x, i) => (x.displayOrder = i + 1));
      onSelected(methods ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderNames = (methods: ReadonlyArray<LoginMethod>): ReactNode =>
    methods.map(x => x.name).join(", ");

  const handleDown =
    (position: number) => (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      value[position] = value.splice(position + 1, 1, value[position])[0];
      value.forEach((x, i) => (x.displayOrder = i + 1));
      onSelected(value);
    };
  const handleUp =
    (position: number) => (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      value[position] = value.splice(position - 1, 1, value[position])[0];
      value.forEach((x, i) => (x.displayOrder = i + 1));
      onSelected(value);
    };

  return (
    <FormControl
      style={props.style}
      variant={variant}
      className={props.className}
    >
      <InputLabel
        id="loginMethod-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="loginMethod-multi-select-label"
        id="loginMethod-multi-select"
        autoWidth={true}
        input={<Input />}
        disabled={props.disabled}
        renderValue={value => renderNames(value as ReadonlyArray<LoginMethod>)}
      >
        {value
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((method, i) => (
            <MenuItem key={i} value={method as any} disableRipple>
              <Checkbox checked />
              <ListItemText
                primary={method.name}
                secondary={
                  !method.allowsIdSwitching ? "Allow Id Switching" : ""
                }
              />
              <Divider
                orientation="vertical"
                flexItem
                variant="middle"
                style={{ margin: "0 5px" }}
              />
              <ListItemIcon
                onClick={e => e.stopPropagation()}
                style={{ minWidth: 0 }}
              >
                <IconButton
                  style={{ minWidth: 0 }}
                  onClick={handleUp(i)}
                  disabled={i === 0}
                  edge="end"
                >
                  <ArrowUpwardIcon />
                </IconButton>
              </ListItemIcon>
              <ListItemIcon
                onClick={e => e.stopPropagation()}
                style={{ minWidth: 0 }}
              >
                <IconButton
                  style={{ minWidth: 0 }}
                  onClick={handleDown(i)}
                  disabled={i === value.length - 1}
                  edge="end"
                >
                  <ArrowDownwardIcon />
                </IconButton>
              </ListItemIcon>
            </MenuItem>
          ))}
        <ListSubheader>Disabled</ListSubheader>
        {loginMethods
          .filter(x => value.every(v => v.id !== x.id))
          .map(x => (
            <MenuItem key={x.id} value={x as any} disableRipple>
              <Checkbox checked={false} />
              <ListItemText
                primary={x.name}
                secondary={!x.allowsIdSwitching ? "Allow Id Switching" : ""}
              />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
